import { Component, DoCheck, Input, OnInit, ViewChild, ViewContainerRef, ChangeDetectorRef } from '@angular/core';
import { LookupService } from '../shared/lookup.service';
import { FilterService } from '../shared/filter.service';
import { Subscription } from 'rxjs/Subscription';
import { BaseSearchRequest, FilterItem } from '../shared/filter-item.model';
import { NavigateWithFiltersService } from '../shared/navigate-with-filters.service';
import { DateUtils } from '../shared/date-utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { InventoryComponent } from '../inventory/inventory.component';
import { FilterODDropDownsComponent } from '../shared/components/filter-od-drop-downs/filter-od-drop-downs.component';
import { ForecastComponent } from '../forecast/forecast.component';
import {FavoriteFilterSelectorComponent} from '../filter-toolbar/favorite-filter-selector/favorite-filter-selector.component';
import {DialogCloseResult, DialogRef, DialogService} from '@progress/kendo-angular-dialog';
import {JourneyMode} from '../group-sales/group-sales-search.model';
import {formatTime24, GroupSalesFilterState, ukToday, validateGroupSalesFilters} from '../group-sales/group-sales-filter-rules';
import {FilterTimePickerValue} from '../shared/components/filter-time-picker/filter-time-picker.component';

@Component({
    selector: 'app-sidebar-nav',
    templateUrl: './sidebar-nav.component.html',
    styleUrls: ['./sidebar-nav.component.scss'],
    providers: [FavoriteFilterSelectorComponent]
})
export class SidebarNavComponent implements OnInit, DoCheck {
    static selectedInventoryGridColumns: any[];
    @ViewChild('rsidMultiSelect') private rsidMultiSelect: any;
    @ViewChild('odSelector') private odSelector: FilterODDropDownsComponent;
    private selectedColValues: any[];
    private selectedFcstColValues: any[];
    @Input() set parentPopupContainer(container: ViewContainerRef) {
        this.popupSettings = { appendTo: container };
    }
    private viewFilterSubscription: Subscription;
    private filterLayoutSubscription: Subscription;
    private viewFilter: any;
    public popupSettings: any;
    public width = '250';
    public opened: boolean;
    public allowToggle = false;
    public classData: FilterItem = new FilterItem();
    public viewTypeData: FilterItem = new FilterItem();
    public portfolioData: FilterItem = new FilterItem();
    public journeyTypeData: FilterItem = new FilterItem();
    public daypartData: FilterItem = new FilterItem();
    public dayOfWeekData: FilterItem = new FilterItem();
    public rsidData: FilterItem = new FilterItem();
    public startPeriod: FilterItem = new FilterItem();
    public endPeriod: FilterItem = new FilterItem();
    public statusTypeData: FilterItem = new FilterItem();
    public startDateRange: number;
    public endDateRange: number;
    public viewLevelSwitch = true;
    public dateSwitch = true;
    public groupSalesBit = false;
    public startDate: Date;
    public endDate: Date;
    public journeyMode: JourneyMode = 'oneWay';
    // Return window defaults to today..today, like a freshly opened outbound range.
    public returnStartDate: Date = ukToday();
    public returnEndDate: Date = ukToday();
    public returnTimePickerValue: FilterTimePickerValue = {timeType: 'departure', startTime: null, endTime: null};
    get isGroupSalesScreen(): boolean {
        return this.filterLayout?.screenName === 'group-sales';
    }
    get showReturnFields(): boolean {
        return this.isGroupSalesScreen && this.journeyMode === 'return';
    }
    get groupSalesMinDate(): Date | null {
        return this.isGroupSalesScreen ? ukToday() : null;
    }
    get groupSalesMinDays(): number | null {
        return this.isGroupSalesScreen ? 0 : null;
    }
    /** Day 0 for the outbound days-out: UK today on Group Sales (matching its minimum), the browser date elsewhere. */
    private get daysOutBase(): Date {
        return this.isGroupSalesScreen ? ukToday() : new Date();
    }
    private get groupSalesFilterState(): GroupSalesFilterState {
        return {
            journeyMode: this.journeyMode,
            startDate: this.startDate,
            endDate: this.endDate,
            time: this.filterTimePickerValue,
            groupSize: this.groupSize,
            returnStartDate: this.returnStartDate,
            returnEndDate: this.returnEndDate,
            returnTime: this.returnTimePickerValue
        };
    }
    get groupSalesHint(): string | null {
        return this.isGroupSalesScreen ? validateGroupSalesFilters(this.groupSalesFilterState).hint : null;
    }
    /**
     * Round-trip return origin. Only an override is stored (null = the default, the outbound destination), so
     * changing the outbound OD moves the default with it. The return destination is always the outbound origin.
     */
    public returnOriginOverride: string | null = null;
    /** Stations with a service to the outbound origin (never the origin itself), always including the default. */
    public returnOriginOptions: {id: string, name: string}[] = [];
    private returnOriginKey: string;
    private returnOriginSource: any[];
    get effectiveReturnOrigin(): string | null {
        const od = this.odSelector?.selectedStationCodes();
        return od ? this.returnOriginOverride ?? od.destination : null;
    }
    get returnRouteLabel(): string {
        const od = this.odSelector?.selectedStationCodes();
        return od ? `Return: ${this.returnOriginOverride ?? od.destination} \u2192 ${od.origin}` : '';
    }
    public onReturnOriginChange(code: string | null): void {
        const od = this.odSelector?.selectedStationCodes();
        this.returnOriginOverride = od && code && code !== od.destination ? code : null;
    }
    /** The OD selector has no change event, so outbound OD changes are spotted here by a cheap key compare. */
    ngDoCheck(): void {
        if (this.isGroupSalesScreen) {
            this.refreshReturnOrigin();
        }
    }
    /** Rebuilds the options when the outbound OD (or the origin lookup) changed, dropping an override that no longer fits. */
    private refreshReturnOrigin(force = false): void {
        const od = this.odSelector?.selectedStationCodes();
        const source = this.odSelector?.originData?.allValues || [];
        const key = od ? `${od.origin}>${od.destination}` : '';
        if (!force && key === this.returnOriginKey && source === this.returnOriginSource) {
            return;
        }
        this.returnOriginKey = key;
        this.returnOriginSource = source;
        if (!od) {
            this.returnOriginOptions = [];
            return; // nothing to judge the override against until both stations are chosen
        }
        const codes = source.filter(s => s.id !== od.origin && (s.destinations || []).includes(od.origin)).map(s => s.id);
        if (!codes.includes(od.destination)) {
            codes.push(od.destination);
        }
        this.returnOriginOptions = codes.sort().map(id => ({id, name: id}));
        const override = this.returnOriginOverride;
        // While the lookup is still loading only the outbound stations themselves can be ruled out.
        if (override !== null && (override === od.origin || override === od.destination ||
            (source.length && !codes.includes(override)))) {
            this.returnOriginOverride = null;
        }
    }
    public groupSize: number;
    public timeVal: any;
    public filterTimePickerValue = {
        timeType: 'departure',
        startTime: null,
        endTime: null
    };
    public filterLayout: any;
    public filtersLoading = false;
    private tempInterval: any;
    public ruleTypeData = [
        {name: 'Min', value: 'Min'},
        {name: 'Max', value: 'Max'},
        {name: 'Min Alloc', value: 'Min_Alloc'},
        {name: 'Max Alloc', value: 'Max_Alloc'}
    ];
    public selectedRuleType: string = null;
    public includeInactiveRules = false;
    public ruleNameFilter = '';

    // Configuration screen: section links shown in place of the filter panel, one per
    // child route under /configuration (see app.module.ts) - key is the route segment.
    private readonly allConfigSections = [
        {key: 'yield-protection', label: 'Yield Protection'},
        {key: 'optimization-schedule', label: 'Optimization Schedule'},
        {key: 'protected-buckets', label: 'Protected Buckets'},
        // Not admin-gated: visible to every user the same as the old OD Groups tab was,
        // gated only by the runtime odGroupingEnabled flag inside the section itself.
        {key: 'od-grouping', label: 'OD Groups'},
        // Not admin-gated: GET /markets/config isn't Admin-only (view access is meant
        // for analysts too, only writes will be restricted once they exist).
        {key: 'markets', label: 'Markets'},
        {key: 'system-settings', label: 'System Settings', adminOnly: true}
    ];
    public configSections = this.allConfigSections.filter(s => !s.adminOnly);

    constructor(private lookupSvc: LookupService, private filterService: FilterService, private navService: NavigateWithFiltersService,
                private favFilter: FavoriteFilterSelectorComponent, private cdr: ChangeDetectorRef, private dialogService: DialogService) {
        this.viewFilterSubscription = this.filterService.selectedFilterView$.subscribe(viewFilter => {
            this.populateFilters(viewFilter);
            const request = this.buildSearchRequest();
            // Group Sales: a view can load a state the Search button would reject (e.g. past fixed dates);
            // checked after populating, so it judges what would be sent. Other screens search as before.
            if (!this.isGroupSalesScreen || this.canApplyFilters()) {
                this.filterService.doSearch(request);
            }
        });

        this.filterLayoutSubscription = this.filterService.filterLayoutView$.subscribe(this.filterLayoutChanging.bind(this));

        navService.filtersChangeTriggered$.subscribe(this.resetFiltersAndSearch.bind(this));

        // Read straight from localStorage, as configuration.component does, rather than
        // through AuthService.getConfig(), which logs the user out when the key is missing.
        const isAdmin = (() => {
            try {
                return JSON.parse(localStorage.getItem('userConfig'))?.userRole === 'Admin';
            } catch {
                return false;
            }
        })();
        this.configSections = this.allConfigSections.filter(s => !s.adminOnly || isAdmin);
    }

    ngOnInit() {
        this.filtersLoading = true;
        this.loadFilters();
    }

    public filterLayoutChanging(filterLayout) {
        this.filterLayout = filterLayout;
        if (['group-sales'].indexOf(this.filterLayout.screenName) >= 0) {
            if (!('groupSalesFilter' in localStorage)) {
                this.favFilter.changeFilterName(null);
            }
        } else {
            if (!('normalFilter' in localStorage)) {
                this.favFilter.changeFilterName(null);
            }
        }
        const skipApplyFilter = 'Y' === localStorage.getItem('skipFiltersOnNavigate');
        if (skipApplyFilter) {
            localStorage.removeItem('skipFiltersOnNavigate');
        }

        if (this.filterLayout.screenName === 'inventory') {
            this.clearFilterValues(this.filterLayout.clear);
        }

        if (!skipApplyFilter && this.canApplyFilters()) {
            // Run the search
            const request = this.buildSearchRequest();
            // Group Sales: buildSearchRequest may have just applied the stored view, so check it again.
            if (!this.isGroupSalesScreen || this.canApplyFilters()) {
                this.filterService.doSearch(request);
            }
        }
    }

    public onSearchClicked(): void {
        this.filterService.doSearch(this.buildSearchRequest(true));
    }

    public onRuleNameFilterChange(value: string): void {
        this.filterService.updateLiveFilter(value);
    }

    public clearFilter(filter: FilterItem): void {
        if (filter) {
            filter.clear();
        }
    }

    public onClearClicked(): void {
        this.groupSize = null;
        this.startDateRange = null;
        this.endDateRange = null;
        this.startDate = null;
        this.endDate = null;
        this.journeyMode = 'oneWay';
        this.returnStartDate = ukToday();
        this.returnEndDate = ukToday();
        this.returnTimePickerValue = {timeType: 'departure', startTime: null, endTime: null};
        this.returnOriginOverride = null;
        this.favFilter.changeFilterName(null);
        if (window.location.href.indexOf('group-sales') > -1) {
            localStorage.removeItem('groupSalesFilter');
        } else {
            localStorage.removeItem('normalFilter');
        }
        this.selectedRuleType = null;
        this.includeInactiveRules = false;
        this.ruleNameFilter = '';
        this.filterService.updateLiveFilter('');
        this.clearFilter(this.dayOfWeekData);
        this.clearFilter(this.portfolioData);
        this.clearFilter(this.classData);
        this.clearFilter(this.daypartData);
        this.clearFilter(this.journeyTypeData);
        this.clearFilter(this.dayOfWeekData);
        this.clearFilter(this.rsidData);
        this.clearFilter(this.statusTypeData);

        if (this.odSelector) {
            this.clearFilter(this.odSelector.destinationData);
            this.clearFilter(this.odSelector.originData);
            this.odSelector.originData.selectedValue = null;
            this.odSelector.destinationData.selectedValue = null;
        }

        if (this.filterTimePickerValue) {
            this.filterTimePickerValue.startTime = null;
            this.filterTimePickerValue.endTime = null;
        }
    }

    public checkHiddenFilter(filterName: string): boolean {
        if (this.filterLayout) {
            return !(this.filterLayout.hidden.findIndex(f => f.name === filterName) >= 0);
        }
    }

    public isToolbarHidden(): boolean {
        return this.filterLayout?.toolbarHidden;
    }

    public buildViewFilter(): any {
        if (window.location.href.indexOf('/inventory') > -1) {
            this.selectedColValues = JSON.parse(localStorage.getItem('selectedInventoryGridColumns'));
        } else {
            this.selectedColValues = JSON.parse(localStorage.getItem('invGridSelectedColumns'));
        }
        if (window.location.href.indexOf('/forecast') > -1) {
            this.selectedFcstColValues = JSON.parse(localStorage.getItem('selectedForecastGridColumns'));
        } else {
            this.selectedFcstColValues = JSON.parse(localStorage.getItem('fcstGridSelectedColumns'));
        }
        if (['group-sales'].indexOf(this.filterLayout.screenName) >= 0) {
            this.groupSalesBit = true;
            this.timeVal = [Object.assign({}, this.filterTimePickerValue)];
        } else {
            this.groupSalesBit = false;
            this.timeVal = [];
            this.groupSize = 0;
        }
        const viewFilter = {
            name: '',
            viewConfiguration: [
                {
                    id: 'classData',
                    name: 'Class Type',
                    values: this.classData.selectedValues
                },
                {
                    id: 'viewTypeData',
                    name: 'View Type',
                    values: this.viewTypeData.selectedValues
                },
                {
                    id: 'portfolioData',
                    name: 'Portfolio',
                    values: this.portfolioData.selectedValues
                },
                {
                    id: 'journeyTypeData',
                    name: 'Journey Type',
                    values: this.journeyTypeData.selectedValues
                },
                {
                    id: 'daypartData',
                    name: 'Daypart',
                    values: this.daypartData.selectedValues
                },
                {
                    id: 'dayOfWeekData',
                    name: 'Day of Week',
                    values: this.dayOfWeekData.selectedValues
                },
                {
                    id: 'rsidData',
                    name: 'RSID',
                    values: this.rsidData.selectedValues
                },
                {
                    id: 'startPeriod',
                    name: 'Start Period',
                    values: this.startPeriod.selectedValues
                },
                {
                    id: 'endPeriod',
                    name: 'End Period',
                    values: this.endPeriod.selectedValues
                },
                {
                    id: 'startDateRange',
                    name: 'Start Date Range',
                    values: this.startDateRange
                },
                {
                    id: 'endDateRange',
                    name: 'End Date Range',
                    values: this.endDateRange
                },
                {
                    id: 'startDate',
                    name: 'Start Date',
                    values: this.startDate
                },
                {
                    id: 'endDate',
                    name: 'End Date',
                    values: this.endDate
                },
                {
                    id: 'viewLevelSwitch',
                    name: 'View Switch',
                    values: this.viewLevelSwitch
                },
                {
                    id: 'dateSwitch',
                    name: 'Date Switch',
                    values: this.dateSwitch
                },
                {
                    id: 'statusTypeData',
                    name: 'Status Type',
                    values: this.statusTypeData.selectedValues
                },
                {
                    id: 'invGridSelectedColumns',
                    name: 'Selected Columns',
                    values: this.selectedColValues
                },
                {
                    id: 'fcstGridSelectedColumns',
                    name: 'Forecast Selected Columns',
                    values: this.selectedFcstColValues
                },
                {
                    id: 'groupSalesFilter',
                    name: 'Group Sales Filter',
                    values: this.groupSalesBit
                },
                {
                    id: 'time',
                    name: 'Time Picker',
                    values: this.timeVal
                },
                {
                    id: 'groupSize',
                    name: 'Group Size',
                    values: this.groupSize
                }
            ]
        };
        if (this.groupSalesBit) {
            const today = ukToday();
            viewFilter.viewConfiguration.push(
                {id: 'journeyMode', name: 'Journey', values: this.journeyMode},
                {
                    // Fixed dates plus days-out, like the outbound startDate/endDate and startDateRange/endDateRange.
                    id: 'returnDates',
                    name: 'Return Dates',
                    values: {
                        startDate: this.returnStartDate,
                        endDate: this.returnEndDate,
                        // A cleared return picker leaves the date null; that view reloads it as today.
                        startDaysOut: this.returnStartDate ? DateUtils.daysDiff(today, this.returnStartDate) : null,
                        endDaysOut: this.returnEndDate ? DateUtils.daysDiff(today, this.returnEndDate) : null
                    }
                },
                {id: 'returnTime', name: 'Return Time Picker', values: [{...this.returnTimePickerValue}]},
                {id: 'returnOrigin', name: 'Return Origin', values: this.returnOriginOverride} // null = the default
            );
        }
        if (this.odSelector) {
            if (['group-sales'].indexOf(this.filterLayout.screenName) >= 0) {
                viewFilter.viewConfiguration.push(...this.odSelector.getCurrentFilterConfig('groupSales'));
            } else {
                viewFilter.viewConfiguration.push(...this.odSelector.getCurrentFilterConfig('other'));
            }
        }
        if (window.location.href.indexOf('/inventory') > -1) {
            localStorage.setItem('invGridSelectedColumns', localStorage.getItem('selectedInventoryGridColumns'));
        }
        if (window.location.href.indexOf('/forecast') > -1) {
            localStorage.setItem('fcstGridSelectedColumns', localStorage.getItem('selectedForecastGridColumns'));
        }
        this.viewFilter = viewFilter;
        return viewFilter;
    }

    public populateFilters(viewFilter): void {
        this.odSelector.setValues(this.getFilterValueFromConfig(viewFilter, 'originData'),
            this.getFilterValueFromConfig(viewFilter, 'destinationData'));

        this.journeyTypeData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'journeyTypeData').values;
        this.daypartData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'daypartData').values;
        this.dayOfWeekData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'dayOfWeekData').values;
        this.rsidData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'rsidData').values;
        this.viewTypeData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'viewTypeData').values;
        this.startDateRange = viewFilter.viewConfiguration.find(fi => fi.id === 'startDateRange').values;
        this.endDateRange = viewFilter.viewConfiguration.find(fi => fi.id === 'endDateRange').values;
        this.viewLevelSwitch = viewFilter.viewConfiguration.find(fi => fi.id === 'viewLevelSwitch').values;
        this.dateSwitch = viewFilter.viewConfiguration.find(fi => fi.id === 'dateSwitch').values;
        this.classData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'classData').values;
        this.startPeriod.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'startPeriod').values;
        this.endPeriod.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'endPeriod').values;
        this.statusTypeData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'statusTypeData').values;
        if (viewFilter.viewConfiguration.find(fi => fi.id === 'groupSalesFilter')) {
            if (viewFilter.viewConfiguration.find(fi => fi.id === 'groupSalesFilter').values) {
                const timeValue = viewFilter.viewConfiguration.find(fi => fi.id === 'time').values;
                this.filterTimePickerValue.timeType = timeValue[0].timeType;
                this.filterTimePickerValue.startTime = new Date(timeValue[0].startTime);
                this.filterTimePickerValue.endTime = new Date(timeValue[0].endTime);
                this.groupSize = viewFilter.viewConfiguration.find(fi => fi.id === 'groupSize').values;
            }
        }
        if (!(['group-sales'].indexOf(this.filterLayout.screenName) >= 0)) {
            this.portfolioData.selectedValues = viewFilter.viewConfiguration.find(fi => fi.id === 'portfolioData').values;
        }
        // Check if the filter has the inventory grid selected columns in it and load the data to inventory metrics selected values.
        if (viewFilter.viewConfiguration.find(fi => fi.id === 'invGridSelectedColumns')) {
            localStorage.setItem('invGridSelectedColumns', JSON.stringify(viewFilter.
                viewConfiguration.find(fi => fi.id === 'invGridSelectedColumns').values));
            if (window.location.href.indexOf('/inventory') > -1) {
                InventoryComponent.loadSelectedColumns(viewFilter.viewConfiguration.find(fi => fi.id === 'invGridSelectedColumns').values);
            }
        } else {
            localStorage.setItem('invGridSelectedColumns', JSON.stringify([]));
            localStorage.setItem('selectedInventoryGridColumns', JSON.stringify([]));
        }
        if (viewFilter.viewConfiguration.find(fi => fi.id === 'fcstGridSelectedColumns')) {
            localStorage.setItem('fcstGridSelectedColumns', JSON.stringify(viewFilter.
                viewConfiguration.find(fi => fi.id === 'fcstGridSelectedColumns').values));
            if (window.location.href.indexOf('/forecast') > -1) {
                ForecastComponent.loadSelectedColumns(viewFilter.viewConfiguration.find(fi => fi.id === 'fcstGridSelectedColumns').values);
            }
        } else {
            localStorage.setItem('fcstGridSelectedColumns', JSON.stringify([]));
            localStorage.setItem('selectedForecastGridColumns', JSON.stringify([]));
        }
        const dateOptionSwitch = viewFilter.viewConfiguration.find(fi => fi.id === 'dateOptionSwitch');
        const today = this.daysOutBase;
        if (this.dateSwitch && dateOptionSwitch && dateOptionSwitch.values) {
            this.startDate = moment(viewFilter.viewConfiguration.find(fi => fi.id === 'startDate').values).toDate();
            this.endDate = moment(viewFilter.viewConfiguration.find(fi => fi.id === 'endDate').values).toDate();
            this.startDateRange = DateUtils.daysDiff(today, this.startDate);
            this.endDateRange = DateUtils.daysDiff(today, this.endDate);
        } else {
            this.startDate = DateUtils.addDays(today, this.startDateRange);
            this.endDate = DateUtils.addDays(today, this.endDateRange);
        }
        if (viewFilter.viewConfiguration.find(fi => fi.id === 'groupSalesFilter')?.values) {
            this.populateReturnFields(viewFilter, !!(this.dateSwitch && dateOptionSwitch && dateOptionSwitch.values));
        }
    }

    /**
     * Loads the return leg of a group sales view. Views saved before RLP-4351 have none of these entries and load as one-way.
     * Return dates follow the outbound rule: fixed dates when the view was saved with fixed dates, otherwise days-out from today.
     */
    private populateReturnFields(viewFilter, fixedDates: boolean): void {
        const find = (id: string) => viewFilter.viewConfiguration.find(fi => fi.id === id)?.values;
        const dates = find('returnDates');
        const returnTime = find('returnTime')?.[0];
        const today = ukToday();
        this.journeyMode = find('journeyMode') || 'oneWay';
        if (dates && !fixedDates && dates.startDaysOut != null && dates.endDaysOut != null) {
            this.returnStartDate = DateUtils.addDays(today, dates.startDaysOut);
            this.returnEndDate = DateUtils.addDays(today, dates.endDaysOut);
        } else {
            this.returnStartDate = dates?.startDate ? moment(dates.startDate).toDate() : ukToday();
            this.returnEndDate = dates?.endDate ? moment(dates.endDate).toDate() : ukToday();
        }
        this.returnTimePickerValue = {
            timeType: returnTime?.timeType || 'departure',
            startTime: returnTime?.startTime ? new Date(returnTime.startTime) : null,
            endTime: returnTime?.endTime ? new Date(returnTime.endTime) : null
        };
        // populateFilters has already applied the view's OD, so the override is judged against that route.
        this.returnOriginOverride = find('returnOrigin') ?? null;
        this.refreshReturnOrigin(true);
    }

    public dropdownTagMapper(tags: any[]): any[] {
        return tags.length < 3 ? tags : [tags];
    }

    private resetFiltersAndSearch(req: BaseSearchRequest) {
        this.portfolioData.selectedValues = !!req.portfolio ? req.portfolio.map(p =>
            ({ id: p, name: p })) : this.portfolioData.selectedValues;
        this.odSelector.clearAndSetValues(req);
        // Resolved to lookup items, not assigned raw: see resolveFilterItems.
        this.journeyTypeData.selectedValues = !!req.journeyType ?
            this.resolveFilterItems(this.journeyTypeData, req.journeyType) : this.journeyTypeData.selectedValues;
        this.daypartData.selectedValues = !!req.daypart ? req.daypart : this.daypartData.selectedValues;
        this.dayOfWeekData.selectedValues = !!req.dayOfWeek ? req.dayOfWeek : this.dayOfWeekData.selectedValues;
        this.rsidData.selectedValues = req.rsid ? req.rsid.map(v => ({ id: v, name: v })) : this.rsidData.selectedValues;
        this.viewTypeData.selectedValues = !!req.viewType ? this.viewTypeData.findByName(req.viewType) : this.viewTypeData.selectedValues;
        // Prefer the incoming class: a deep link into a First-class journey must not land
        // on a Standard search. Falls back to the previous behaviour when absent so the
        // other navigateWithFilters callers are unaffected.
        this.classData.selectedValues = !!req.passengerClass ? this.resolveFilterItem(this.classData, req.passengerClass) :
            (this.classData.selectedValues !== null && this.classData.selectedValues !== undefined ?
                this.classData.selectedValues : this.resolveFilterItem(this.classData, 'standard'));
        // canApplyFilters() rejects the business-rules-priorities screen without a rule
        // type, and buildSearchRequest sends it to an API that requires it.
        this.selectedRuleType = !!req.ruleType ? req.ruleType : this.selectedRuleType;
        this.startPeriod.selectedValues = [];
        this.endPeriod.selectedValues = [];
        this.statusTypeData.selectedValues = !!req.statusType ?
            [this.statusTypeData.findByName(req.statusType)] : this.statusTypeData.selectedValues;

        this.dateSwitch = true;
        this.startDate = req.startDate ? req.startDate : this.startDate;
        this.endDate = req.endDate ? req.endDate : this.endDate;
        this.startDateRange = DateUtils.daysDiff(this.daysOutBase, this.startDate);
        this.endDateRange = DateUtils.daysDiff(this.daysOutBase, this.endDate);

        this.filterService.doSearch(this.buildSearchRequest());
    }

    public displayToggle(): boolean {
        return this.allowToggle;
    }

    public rsidValueNormalizer = (text$: Observable<string>) => text$.pipe(map((text: string) => {
        // search for matching item to avoid duplicates

        // search in values
        const matchingValue: any = this.rsidData.selectedValues.find((item: any) => {
            return item.name.toLowerCase() === text.toLowerCase();
        });

        if (matchingValue) {
            return matchingValue; // return the already selected matching value and the component will remove it
        }

        // search in data
        const matchingItem: any = this.rsidData.allValues.find((item: any) => {
            return item.name.toLowerCase() === text.toLowerCase();
        });

        if (matchingItem) {
            return matchingItem;
        } else {
            return {
                id: text,
                name: text
            };
        }
    }))

    private loadFilters(): void {
        this.portfolioData.loadAllValues(this.lookupSvc.loadPortfolio());
        this.journeyTypeData.loadAllValues(this.lookupSvc.loadJourneyTypes());
        this.daypartData.loadAllValues(this.lookupSvc.loadDayparts());
        this.dayOfWeekData.loadAllValues(this.lookupSvc.loadDaysOfWeek());
        this.rsidData.loadAllValues(this.lookupSvc.loadRSIDs());

        const viewTypesLoader = this.lookupSvc.loadViewTypes();
        this.viewTypeData.loadAllValues(viewTypesLoader);
        // Also, when view-types are loaded - default 'Daily' as selected value
        viewTypesLoader.subscribe(result => {
            this.viewTypeData.selectedValues = result.data.find(o => (o.name === 'Daily'));
        });

        this.classData.loadAllValues(this.lookupSvc.loadClassTypes());
        this.startPeriod.loadAllValues(this.lookupSvc.loadPeriods(true)); // , this.periodSorter);
        this.endPeriod.loadAllValues(this.lookupSvc.loadPeriods(false)); // , this.periodSorter);
        this.statusTypeData.loadAllValues(this.lookupSvc.getStatusTypes());
        // this.startDateRange = 0;
        // this.endDateRange = 0;
        // this.viewTypeData.selectedValues = !!req.viewType ? this.viewTypeData.findByName(req.viewType)
        // now wait for all above to load to set this.filtersLoading
        this.tempInterval = setInterval(() => {
            if (this.checkFiltersLoaded()) {
                clearInterval(this.tempInterval);
                this.filtersLoading = false;
            }
        }, 500);
    }
    private checkFiltersLoaded() {
        // odSelector never mounts on /configuration/* routes - the filter panel it lives
        // in is hidden there in favour of the Configuration section links (see the
        // template's *ngIf on .spinner-wrapper). Left unguarded, this ran every 500ms
        // forever on those routes and threw on every tick.
        if (!this.portfolioData.valuesLoaded || !this.odSelector?.valuesLoaded() ||
            !this.journeyTypeData.valuesLoaded || !this.daypartData.valuesLoaded || !this.dayOfWeekData.valuesLoaded ||
            !this.rsidData.valuesLoaded || !this.viewTypeData.valuesLoaded || !this.classData.valuesLoaded ||
            !this.startPeriod.valuesLoaded || !this.endPeriod.valuesLoaded || !this.statusTypeData.valuesLoaded) {
            return false;
        }
        return true;
    }

    private buildSearchRequest(isNewSearch = false): BaseSearchRequest {
        const searchRequest = new BaseSearchRequest();
        if (['group-sales'].indexOf(this.filterLayout.screenName) >= 0) {
            if ('groupSalesFilter' in localStorage) {
                const getFilter = JSON.parse(localStorage.getItem('groupSalesFilter'));
                if (!('groupSalesPageLoaded' in localStorage)) {
                    localStorage.setItem('groupSalesPageLoaded', '1');
                    this.populateFilters(getFilter);
                } else {
                    const gsLoaded =  localStorage.getItem('groupSalesPageLoaded');
                    if (!(gsLoaded === '1')) {
                        this.populateFilters(getFilter);
                    }
                }
                this.favFilter.changeFilterName(getFilter);
                this.filterService.selectFilterView(getFilter.name);
             } else {
                this.favFilter.changeFilterName(null);
            }
        } else {
            if ('normalFilter' in localStorage) {
                const getFilter = JSON.parse(localStorage.getItem('normalFilter'));
                const prevPage = localStorage.getItem('prevPage');
                if (prevPage.indexOf('group-sales') > -1 ) {
                    this.populateFilters(getFilter);
                    localStorage.setItem('groupSalesPageLoaded', 'false');
                    localStorage.setItem('prevPage', '');
                    this.favFilter.changeFilterName(getFilter);
                    this.filterService.selectFilterView(getFilter.name);
                }
                this.favFilter.changeFilterName(getFilter);
                this.filterService.selectFilterView(getFilter.name);
            } else {
                this.favFilter.changeFilterName(null);
            }
        }
        if (this.dateSwitch) {
            searchRequest.startDate = this.startDate;
            searchRequest.endDate = this.endDate;
        } else {
            searchRequest.startDate = this.startPeriod.selectedValues.id;
            searchRequest.endDate = this.endPeriod.selectedValues.id;
        }

        if (['dashboard', 'forecast'].indexOf(this.filterLayout.screenName) >= 0) {
            searchRequest.viewLevel = this.viewLevelSwitch; // OD: true, Leg: False
            searchRequest.viewType = this.viewTypeData.selectedValues.name;
            searchRequest.daypart = this.daypartData.selectedValues.map(dp => dp.id);
        }

        if (['inventory'].indexOf(this.filterLayout.screenName) >= 0) {
            searchRequest.recordStatus = this.statusTypeData.selectedValues.map(st => st.id);
        }

        if (['dashboard', 'forecast', 'business-rules', 'business-rules-priorities'].indexOf(this.filterLayout.screenName) >= 0) {
            searchRequest.journeyType = this.journeyTypeData.selectedValues.map(jt => jt.id);
        }
        if (this.filterLayout.screenName === 'business-rules-priorities') {
            searchRequest.ruleType = this.selectedRuleType;
        }
        if (this.filterLayout.screenName === 'business-rules') {
            searchRequest.includeInactiveRules = this.includeInactiveRules;
            searchRequest.ruleNames = [];
        }
        if (['group-sales'].indexOf(this.filterLayout.screenName) >= 0) {
            searchRequest.time = Object.assign({}, this.filterTimePickerValue);
            searchRequest.time.startTime = formatTime24(searchRequest.time.startTime as Date);
            searchRequest.time.endTime = formatTime24(searchRequest.time.endTime as Date);
            searchRequest.groupSize = this.groupSize;
            if (this.journeyMode === 'return') {
                searchRequest.returnJourney = {
                    startDate: this.returnStartDate,
                    endDate: this.returnEndDate,
                    time: {
                        timeType: this.returnTimePickerValue.timeType,
                        startTime: formatTime24(this.returnTimePickerValue.startTime as Date),
                        endTime: formatTime24(this.returnTimePickerValue.endTime as Date)
                    }
                };
                this.refreshReturnOrigin(true); // the OD may have changed since the last change-detection pass
                if (this.returnOriginOverride !== null) { // omitted for the default, so the API reverses the route
                    searchRequest.returnJourney.origin = this.returnOriginOverride;
                }
            }
        }
        searchRequest.passengerClass = this.classData.selectedValues.id;
        searchRequest.portfolio = this.portfolioData.selectedValues.map(port => port.id);

        if (this.odSelector) {
            const selectedODs = this.odSelector.getSelectedValues(this.filterLayout?.journeyFilterConfig);
            searchRequest.origin = selectedODs.origin;
            searchRequest.destination = selectedODs.destination;
        }

        searchRequest.dayOfWeek = this.dayOfWeekData.selectedValues.map(dow => dow.id);
        searchRequest.rsid = this.handleWildCardFilterValues(this.rsidData.allValues,
            this.rsidData.selectedValues).map(rsid => rsid.id);
        searchRequest.startDateRange = this.startDateRange;
        searchRequest.endDateRange = this.endDateRange;
        searchRequest.isNewSearch = isNewSearch;

        return searchRequest;
    }

    handleFilterStart(value) {
        this.startPeriod.filterItems(value);
    }

    handleFilterEnd(value) {
        this.endPeriod.filterItems(value);
    }

    handleFilterRSID(value){
        if (value.trim().length){
            // Split the values on both comma and space.
            const values = value.split(/[\s,]+/).map((x) => {return x.toUpperCase()})
            
            if (values.length > 1)
                this.rsidData.filterItemsByFn((s) => values.includes(s.id) , false)
            else
                this.rsidData.filterItemsByFn((s) =>s.id.toLowerCase().indexOf(value.toLowerCase()) !== -1, false)
        }else{
            this.rsidData.filteredValues = this.rsidData.allValues
        }
        this.cdr.detectChanges();
    }

    startPeriodChange(value: any) {
        // eslint-disable-next-line radix
        this.endPeriod.filterItemsByFn((s) => s.id > value.id, true);
    }

    canApplyFilters() {
        const notBusinessRulesScreen = (this.filterLayout !== undefined &&
            this.filterLayout.screenName !== 'business-rules' &&
            this.filterLayout.screenName !== 'business-rules-priorities');
        const notGroupSalesScreen = (this.filterLayout !== undefined &&
            this.filterLayout.screenName !== 'group-sales');
        const requiresClass = notBusinessRulesScreen ||
            (this.filterLayout !== undefined && this.filterLayout.screenName === 'business-rules-priorities');
        if (!this.classData.selectedValues?.id && requiresClass) {
            return false;
        }
        if (this.filterLayout?.screenName === 'business-rules-priorities' && !this.selectedRuleType) {
            return false;
        }
        if ((!this.portfolioData.selectedValues || this.portfolioData.selectedValues.length === 0) &&
            (!this.rsidData.selectedValues || this.rsidData.selectedValues.length === 0) &&
            (notBusinessRulesScreen && notGroupSalesScreen ||
             this.filterLayout?.screenName === 'business-rules-priorities')) {
            return false;
        }
        if (!notGroupSalesScreen && (this.filterTimePickerValue.startTime === null ||
            this.filterTimePickerValue.endTime === null)) {
            return false;
        }
        if (!notGroupSalesScreen && (this.groupSize === null || this.groupSize === undefined)) {
            return false;
        }
        if (!notGroupSalesScreen && !validateGroupSalesFilters(this.groupSalesFilterState).valid) {
            return false;
        }
        if (!notGroupSalesScreen && !this.odSelector.isValid()) {
            return false;
        }
        if (this.dateSwitch && (!this.startDate || !this.endDate) && notBusinessRulesScreen) {
            return this.startDateRange !== undefined && this.endDateRange !== undefined;
        }
        if (!this.dateSwitch && (!this.startPeriod.selectedValues.id || !this.endPeriod.selectedValues.id) &&
            notBusinessRulesScreen) {
            return false;
        }
        if ((this.filterLayout !== undefined && this.filterLayout.screenName === 'dashboard') &&
            (!this.viewTypeData.selectedValues || !this.viewTypeData.selectedValues.name)) {
            return false;
        }
        return true;
    }

    canClearFilters() {
        if (this.portfolioData.selectedValues && this.portfolioData.selectedValues.length > 0) {
            return true;
        }
        if (this.classData) {
            if (Array.isArray(this.classData.selectedValues)) {
                if (this.portfolioData.selectedValues.length > 0) {
                    return true;
                }
            } else {
                return true;
            }
        }
        if (this.startDate || this.endDate) {
            return true;
        }
        if (this.daypartData && this.daypartData.selectedValues.length > 0) {
            return true;
        }
        if (this.journeyTypeData && this.journeyTypeData.selectedValues.length > 0) {
            return true;
        }
        if (this.dayOfWeekData && this.dayOfWeekData.selectedValues.length > 0) {
            return true;
        }
        if (this.rsidData && this.rsidData.selectedValues.length > 0) {
            return true;
        }
        if (this.statusTypeData && this.statusTypeData.selectedValues.length > 0) {
            return true;
        }
        if (this.odSelector) {
            if (this.odSelector.destinationData && this.odSelector.destinationData.selectedValues.length > 0) {
                return true;
            }

            if (this.odSelector.destinationData.selectedValue) {
                return true;
            }

            if (this.odSelector.originData && this.odSelector.originData.selectedValues.length > 0) {
                return true;
            }

            if (this.odSelector.originData.selectedValue) {
                return true;
            }
        }
        if (this.filterTimePickerValue) {
            if (this.filterTimePickerValue.startTime !== null || this.filterTimePickerValue.endTime !== null) {
                return true;
            }
        }
        if (this.groupSize) {
            return true;
        }
    }

    onStartDateChange(newDt: Date) {
        // update startDateRange
        this.startDateRange = DateUtils.daysDiff(this.daysOutBase, newDt);
        if (newDt > this.endDate){
            this.endDateRange = DateUtils.daysDiff(this.daysOutBase, newDt);
            this.endDate = newDt
        }
    }

    onEndDateChange(newDt: Date) {
        // update startDateRange
        if (newDt < this.startDate){
            const dialog: DialogRef = this.dialogService.open({
                title: 'Invalid End Date !!!',
                content: 'End Date cannot be prior to Start Date.',
                actions: [
                    {text: 'OK', primary: true}
                ]
            });
            dialog.result.subscribe((result) => {
                if ((result instanceof DialogCloseResult) || (result.text === 'OK')) {
                    this.endDateRange = this.startDateRange;
                    this.endDate = DateUtils.addDays(this.daysOutBase, this.endDateRange);
                }
            });
        }
        this.endDateRange = DateUtils.daysDiff(this.daysOutBase, newDt);
    }

    onStartDateRangeChange = (newVal: number) => {
        this.startDate = DateUtils.addDays(this.daysOutBase, newVal);
        if (newVal > this.endDateRange){
            this.endDate = DateUtils.addDays(this.daysOutBase, newVal);
            this.endDateRange = newVal;
        }
        // console.log('Start Date Range Changed to ' + newVal);
    }

    onEndDateRangeChange = (event: any) => {
        const newVal = event.target.value;
        if ((this.startDateRange !== undefined) && (newVal < this.startDateRange)){
            const dialog: DialogRef = this.dialogService.open({
                title: 'Invalid End Date !!!',
                content: 'End Date cannot be prior to Start Date.',
                actions: [
                    {text: 'OK', primary: true}
                ]
            });
            dialog.result.subscribe((result) => {
                if ((result instanceof DialogCloseResult) || (result.text === 'OK')) {
                    this.endDateRange = this.startDateRange;
                    this.endDate = DateUtils.addDays(this.daysOutBase, this.endDateRange);
                }
            });
        }
        this.endDate = DateUtils.addDays(this.daysOutBase, this.endDateRange);
        // console.log('Start Date Range Changed to ' + newVal);
    }

    public rsidMultiSelectChange(): void {
        // TODO: Remove hack once KendoUI fixes bug with multi select
        // https://github.com/telerik/kendo-angular/issues/2753
        this.rsidMultiSelect.text = '';
        setTimeout(() => {
            this.rsidMultiSelect.focus();
        });
    }

    private clearFilterValues(filterNames: string[]): void {
        filterNames.forEach(filterName => {
            if (filterName in this) {
                this[filterName].selectedValues = [];
            }
        });
    }

    private getFilterValueFromConfig(viewFilter: any, filterId: string): any {
        return viewFilter.viewConfiguration.find(fi => fi.id === filterId).values;
    }

    /**
     * Resolves incoming filter ids to the lookup's own items.
     *
     * The filter controls are bound with [textField]/[valueField] and no [valuePrimitive],
     * so their value must be objects drawn from allValues. Assigning raw id strings makes
     * Kendo raise a value-type error during change detection -- which freezes controls in
     * the panel -- and makes buildSearchRequest's .map(x => x.id) yield undefined.
     *
     * Ids absent from the lookup are dropped rather than passed through: a single undefined
     * in the value array breaks the control just as badly as a string would.
     *
     * NOTE: daypartData and dayOfWeekData are still assigned raw in resetFiltersAndSearch.
     * That is harmless only because no navigateWithFilters caller populates them yet -- the
     * first one that does must route through here too.
     */
    private resolveFilterItems(filterItem: FilterItem, ids: any[]): any[] {
        if (!filterItem.allValues) {
            return [];
        }
        return ids.map(id => filterItem.findById(id)).filter(item => !!item);
    }

    /** Single-value form of resolveFilterItems, for the dropdowns. */
    private resolveFilterItem(filterItem: FilterItem, id: string): any {
        return filterItem.allValues ? filterItem.findById(id) : null;
    }

    private handleWildCardFilterValues(allData: any, selectedData: any): any {
        const wildCards = selectedData.filter(item => item.id.includes('*'));
        if (!wildCards) {
            return [];
        }
        const nonWild = selectedData.filter(item => !item.id.includes('*'));
        const filterBy = str => allData.filter(
            item => {
                const escapeRegex = (str$) => str$.replace(/\*/g, '\\$1');
                return new RegExp('^' + str.split('*').map(escapeRegex).join('.*') + '$').test(item.id);
            }
        );

        let records = [];
        wildCards.forEach(wildCard => {
            records = records.concat(filterBy(wildCard.id));
        });
        records = records.concat(nonWild);

        // If wild card records are provided, but non could be deterimied from the existing data
        // add dummy record to prevent filtering on whole data set.
        if (wildCards.length > 0 && records.length === 0) {
            records = ['NO_RECORDS'];
        }

        return [...new Set(records)];
    }
}
